import { AsyncPipe, CurrencyPipe, DatePipe } from '@angular/common';
import { Component, HostListener, computed, effect, inject, signal, viewChild } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import {
  NgbActiveModal,
  NgbDatepickerModule,
  NgbModal,
  NgbModalOptions,
  NgbNavChangeEvent,
  NgbNavModule,
  NgbTypeaheadModule
} from '@ng-bootstrap/ng-bootstrap';
import {
  catchError,
  combineLatest,
  debounceTime,
  defer,
  distinctUntilChanged,
  EMPTY,
  finalize,
  map,
  Observable,
  of,
  switchMap,
  tap
} from 'rxjs';
import { LoaderComponent } from '@app/shared/components/loader/loader.component';
import {
  CalculatedVehicleCategoryModel,
  ReservationCreateModel,
  ReservationDetailsModel,
  ReservationStatus,
  ReservationStatusesDisplayNames,
  ReservationType,
  ReservationUpdateModel,
  ReservationVersion
} from '../reservations.model';
import { ReservationsService } from '../reservations.service';
import { ReservationDriverDetailsModel } from '../reservation-drivers/reservation-drivers.model';
import { ReservationValueDetailsModel } from '../reservation-values/reservation-values.model';
import { ReservationValuesComponent } from '../reservation-values/reservation-values.component';
import { VehicleCategoryService } from '@app/core/services/vehicle-category.service';
import { LocationService } from '@app/core/services/location.service';
import { ReservationDriversComponent } from '../reservation-drivers/reservation-drivers.component';
import { ToastsService } from '@app/shared/components/toasts/toasts.service';
import { getErrorMessages } from '@app/shared/utilities/errorMessages.util';
import { RentalAgreementsUpsertDialogComponent } from '../../rental-agreements/rental-agreements-upsert-dialog/rental-agreements-upsert-dialog.component';
import { ReservationDriversUpsertDialogComponent } from '../reservation-drivers/reservation-drivers-upsert-dialog/reservation-drivers-upsert-dialog.component';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { RentalAgreementsService } from '../../rental-agreements/rental-agreements.service';
import { CreateResponseModel } from '@app/core/models/crud/create.model';
import { BrokerService } from '@app/core/services/broker.service';
import { CopyToClipboardDirective } from '@app/shared/directives/copy-to-clipboard.directive';
import { DriverCardComponent } from '@app/shared/components/driver-card/driver-card.component';
import { DateAgoPipe } from '@app/pipes/date-ago.pipe';
import { BrokerAutocompleteDirective } from '@app/shared/directives/broker-autocomplete.directive';
import { VehicleCategoryListItem } from '@app/core/models/common.model';
import { CurrencyService } from '@app/core/services/currency.service';
import { AuthService } from '@app/core/services/auth.service';
import { SplitterModule } from 'primeng/splitter';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { SkeletonModule } from 'primeng/skeleton';
import { Router } from '@angular/router';
import { MenuModule } from 'primeng/menu';
import { AccordionModule } from 'primeng/accordion';
import { ReservationStatusBadgeComponent } from '@app/shared/components/reservation-status-badge/reservation-status-badge.component';
import { AutoCompleteModule, AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { LocationAutocompleteDirective } from '@app/shared/directives/location-autocomplete.directive';
import { SelectModule } from 'primeng/select';
import { VehicleCategorySelectComponent } from '@app/shared/components/vehicle-category-select/vehicle-category-select.component';
import { newClientUid, toBackendUid } from '@app/shared/utilities/client-uid.util';
import { ReservationCalculatedDataLoader } from './reservation-calculated-data.loader';
import { ReservationHistoryComponent } from '../reservation-history/reservation-history.component';

const DEFAULT_RENTAL_DAYS = 3;
const DEFAULT_RENTAL_HOUR = 10;

function countAdditionalDrivers(drivers: ReservationDriverDetailsModel[]): number {
  return drivers.filter((d) => d.isAdditionalDriver).length;
}

// Drop-off date must be after the pick-up date.
function dropOffAfterPickupValidator(control: AbstractControl): ValidationErrors | null {
  const dateIn = control.value as Date | null;
  const dateOut = control.parent?.get('dateOut')?.value as Date | null;
  if (!dateIn || !dateOut) return null;
  return dateIn > dateOut ? null : { dropOffBeforePickup: true };
}

interface ReservationGeneralForm {
  status: FormControl<number>;
  dateOut: FormControl<Date | null>;
  dateIn: FormControl<Date | null>;
  pickupLocationUid: FormControl<{ uid: string; selectedLabel: string } | null>;
  dropOffLocationUid: FormControl<{ uid: string; selectedLabel: string } | null>;
  vehicleCategoryUid: FormControl<string | null>;
  servedVehicleCategoryUid: FormControl<string | null>;
  brokerClientId: FormControl<{ code: number; selectedLabel: string } | null>;
  brokerReference: FormControl<string>;
  observations: FormControl<string | null>;
  // Create mode: the extras the user picks from the selected car group's calculated-data catalog.
  extraItems: FormControl<ReservationValueDetailsModel[]>;
}

interface ReservationPaymentForm {
  currency: FormControl<string>;
  taxRate: FormControl<number | null>;
  totalTaxValue: FormControl<number | null>;
  cdwExcess: FormControl<number>;
  tpExcess: FormControl<number>;
  areKmsIncluded: FormControl<boolean>;
  areKmsFreePerDay: FormControl<boolean>;
  freeKmsPerDay: FormControl<number>;
  additionalKmsPrice: FormControl<number>;
  discountAmount: FormControl<number>;
}

interface ReservationForm {
  general: FormGroup<ReservationGeneralForm>;
  payment: FormGroup<ReservationPaymentForm>;
}

export type ReservationUpsertDialogConfig =
  | { mode: 'create' }
  | { mode: 'update'; uid: string }
  | { mode: 'readonly'; uid: string; version?: ReservationVersion }
  | { mode: 'readonly'; number: string; version?: ReservationVersion };

@Component({
  selector: 'app-reservations-upsert-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AsyncPipe,
    NgbNavModule,
    LoaderComponent,
    ReservationValuesComponent,
    ReservationDriversComponent,
    NgbDatepickerModule,
    DatePipe,
    NgbTypeaheadModule,
    CopyToClipboardDirective,
    DriverCardComponent,
    DateAgoPipe,
    CurrencyPipe,
    SplitterModule,
    DatePickerModule,
    IftaLabelModule,
    SkeletonModule,
    MenuModule,
    AccordionModule,
    ReservationStatusBadgeComponent,
    AutoCompleteModule,
    LocationAutocompleteDirective,
    BrokerAutocompleteDirective,
    SelectModule,
    VehicleCategorySelectComponent,
    ReservationHistoryComponent
  ],
  templateUrl: './reservations-upsert-dialog.component.html',
  host: { class: 'd-flex flex-fill flex-column' },
  providers: [ReservationCalculatedDataLoader]
})
export class ReservationsUpsertDialogComponent {
  private _calcData = inject(ReservationCalculatedDataLoader);
  private _modal = inject(NgbActiveModal);
  private _formBuilder = inject(NonNullableFormBuilder);
  private _reservationsService = inject(ReservationsService);
  private _raService = inject(RentalAgreementsService);
  private _vehicleCategoryService = inject(VehicleCategoryService);
  private _locationService = inject(LocationService);
  private _toastsService = inject(ToastsService);
  private _modalService = inject(NgbModal);
  private _brokerService = inject(BrokerService);
  private _currencyService = inject(CurrencyService);
  private _authService = inject(AuthService);
  private _router = inject(Router);

  // View queries
  valuesComponent = viewChild(ReservationValuesComponent);
  driversComponent = viewChild(ReservationDriversComponent);

  private _raCreationConfiguredForCountry = false;

  private _driversModified = false;
  private _extrasModified = false;
  private _selectedVehicleCategory = signal<string | null>(null);

  config = signal<ReservationUpsertDialogConfig>({ mode: 'create' });
  mode = computed(() => this.config()?.mode ?? 'readonly');
  reservationVersion = computed(() => {
    const config = this.config();
    return config?.mode === 'readonly' && config.version ? config.version : 'latest';
  });

  readonly drivers = signal<ReservationDriverDetailsModel[]>([]);
  readonly valueItems = signal<ReservationValueDetailsModel[]>([]);

  readonly mainDriver = computed(() => this.drivers().find((d) => !d.isAdditionalDriver) ?? null);

  uid = computed(() => {
    const config = this.config();
    switch (config?.mode) {
      case 'create':
        return newClientUid();
      case 'update':
        return config.uid;
      case 'readonly':
        return this.item()!.uid;
      default:
        return newClientUid();
    }
  });

  form: FormGroup<ReservationForm>;
  readonly returnMinDate = signal<Date | null>(null);
  readonly pickupDefaultDate = this.atDefaultHour(new Date());
  readonly returnDefaultDate = signal(this.atDefaultHour(new Date()));
  // Autocomplete suggestions for the extra-items picker, filtered from the current car group's catalog.
  extraSuggestions: ReservationValueDetailsModel[] = [];
  loading = false;
  errors: string[] | null = null;
  readonly item = signal<ReservationDetailsModel | null>(null);
  hasUnsavedData = false;
  historyExpanded = 0;
  loadingValueItems = false;
  valueItemsError: string | null = null;

  private readonly _calculatedVehicleCategories = this._calcData.vehicleCategories;
  private readonly _allVehicleCategories = signal<VehicleCategoryListItem[]>([]);

  readonly availableVehicleCategories = computed<CalculatedVehicleCategoryModel[]>(() => {
    const calculated = this._calculatedVehicleCategories();
    const item = this.item();
    if (!item) return calculated;

    const catalog = this._allVehicleCategories();
    const missing: CalculatedVehicleCategoryModel[] = [];
    for (const uid of [item.vehicleCategoryUid, item.servedVehicleCategoryUid]) {
      if (!uid) continue;
      if (calculated.some((c) => c.uid === uid) || missing.some((c) => c.uid === uid)) continue;
      const known = catalog.find((c) => c.uid === uid);
      missing.push({ uid, code: known?.code ?? uid, name: known?.name ?? '', availableExtras: [] });
    }
    return missing.length ? [...calculated, ...missing] : calculated;
  });

  readonly availableValueItems = computed(() => {
    return (
      this.availableVehicleCategories().find((x) => x.uid === this._selectedVehicleCategory())?.availableExtras ?? []
    );
  });

  private _lastLoadSignature: string | null = null;
  lockMenu = computed(() =>
    this.config()?.mode === 'readonly'
      ? [{ label: 'Unlock', icon: 'fa-solid fa-unlock fa-fw', command: () => this.unlockEditing() }]
      : [{ label: 'Lock', icon: 'fa-solid fa-lock fa-fw', command: () => this.lockEditing() }]
  );

  private _selectedReservation$ = of(true).pipe(
    switchMap(() => {
      const config = this.config();
      if (!config) return of(null);
      return defer(() => {
        if (config.mode === 'readonly' && 'number' in config && config.number) {
          return this._reservationsService.getReservationDetailsByNumber(config.number);
        }
        if ('uid' in config && config.uid) {
          return this._reservationsService.getReservationDetails(config.uid, this.reservationVersion());
        }
        return of(null);
      }).pipe(
        map((reservation) => {
          this.item.set(reservation ?? null);
          this.initialize();
        })
      );
    })
  );

  vm$ = combineLatest([
    this._currencyService.getAllCurrencies(),
    this._selectedReservation$,
    this._vehicleCategoryService.getAllVehicleCategories().pipe(tap((c) => this._allVehicleCategories.set(c)))
  ]).pipe(map(([currencies, , vehicleCategories]) => ({ currencies, vehicleCategories })));

  constructor() {
    this.form = this._formBuilder.group({
      general: this._formBuilder.group({
        status: this._formBuilder.control(0, [Validators.required]),
        dateOut: this._formBuilder.control<Date | null>(null, [Validators.required]),
        dateIn: this._formBuilder.control<Date | null>(null, [Validators.required, dropOffAfterPickupValidator]),
        pickupLocationUid: this._formBuilder.control<{ uid: string; selectedLabel: string } | null>(null, [
          Validators.required
        ]),
        dropOffLocationUid: this._formBuilder.control<{ uid: string; selectedLabel: string } | null>(null, [
          Validators.required
        ]),
        vehicleCategoryUid: this._formBuilder.control<string | null>(null, [Validators.required]),
        servedVehicleCategoryUid: this._formBuilder.control<string | null>(null),
        brokerClientId: this._formBuilder.control<{ code: number; selectedLabel: string } | null>(null, [
          Validators.required
        ]),
        brokerReference: this._formBuilder.control('', [Validators.required, Validators.maxLength(30)]),
        observations: this._formBuilder.control<string | null>(''),
        extraItems: this._formBuilder.control<ReservationValueDetailsModel[]>([])
      }),
      payment: this._formBuilder.group({
        currency: this._formBuilder.control('', [Validators.required, Validators.maxLength(3)]),
        taxRate: this._formBuilder.control<number | null>(null, [Validators.required]),
        totalTaxValue: this._formBuilder.control<number | null>(null, [Validators.required]),
        cdwExcess: this._formBuilder.control(0, [Validators.required]),
        tpExcess: this._formBuilder.control(0, [Validators.required]),
        areKmsIncluded: this._formBuilder.control(false, [Validators.required]),
        areKmsFreePerDay: this._formBuilder.control(false, [Validators.required]),
        freeKmsPerDay: this._formBuilder.control(0, [Validators.required]),
        additionalKmsPrice: this._formBuilder.control(0, [Validators.required]),
        discountAmount: this._formBuilder.control(0, [Validators.required])
      })
    });

    this.form.valueChanges.subscribe(() => {
      this.hasUnsavedData = this.form.dirty;
    });

    const general = this.form.controls.general.controls;

    general.dateOut.valueChanges.subscribe((dateOut) => {
      this.returnMinDate.set(dateOut ? this.addMinutes(dateOut, 1) : null);
      this.returnDefaultDate.set(this.atDefaultHour(dateOut ? this.addDays(dateOut, DEFAULT_RENTAL_DAYS) : new Date()));
      general.dateIn.updateValueAndValidity({ emitEvent: false });

      if (this.mode() === 'create' && dateOut && general.dateIn.pristine) {
        general.dateIn.setValue(this.addDays(dateOut, DEFAULT_RENTAL_DAYS));
      }
    });

    // Create mode: the extras belong to the selected booked car group, so a group change drops the
    // current selection (its options no longer apply).
    general.vehicleCategoryUid.valueChanges.subscribe((value) => {
      this._selectedVehicleCategory.set(value);
      if (this.mode() === 'create') general.extraItems.setValue([]);
    });

    // Create mode: reflect the picked extras on the (read-only) values grid — they are the accepted
    // value items sent on create.
    general.extraItems.valueChanges.subscribe((extras) => {
      if (this.mode() !== 'create') return;
      this.valueItems.set((extras ?? []).map((e) => ({ ...e, uid: e.uid || e.code, isAccepted: true })));
      this.valuesComponent()?.refresh();
    });

    // In create mode load the available car groups (and their extras) as soon as the booking
    // inputs are filled, so the car group fields can offer them.
    this.form.controls.general.valueChanges
      .pipe(
        debounceTime(400),
        map(() => this.calculatedDataSignature()),
        distinctUntilChanged()
      )
      .subscribe(() => this.tryLoadCalculatedData());

    effect(() => {
      // The stores start empty on a fresh dialog instance, so create mode needs no reset here
      // (writing signals inside an effect is disallowed anyway).
      if (this.mode() === 'create') {
        this.enableCreatingForm();
        return;
      }
      if (this.mode() === 'readonly') {
        this.form.disable();
        return;
      }
      this.enableUpdatingForm();
    });
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent) {
    if (this.hasUnsavedData) {
      $event.preventDefault();
      $event.returnValue = ''; // Required for Chrome
    }
  }

  canModify() {
    const item = this.item();
    if (!item) return false;
    const editableStatus = item.status === ReservationStatus.Confirmed || item.status === ReservationStatus.Pending;
    // Once the pick-up date has passed only AddCar staff may still modify the reservation.
    return editableStatus && (this._authService.IsAddCarUser || !this.hasBeenPickedUp());
  }

  unlockEditing() {
    if (this.mode() === 'readonly' && this.reservationVersion() === 'latest' && this.canModify()) {
      this.config.set({ mode: 'update', uid: this.item()!.uid });
      if (!this.hasBeenPickedUp()) {
        this.autoLoadCalculatedData();
      }
    }
  }

  lockEditing() {
    if (this.mode() === 'update') {
      // The config change triggers a re-fetch, which re-seeds the drafts from the server and
      // thereby discards the staged edits.
      this.config.set({ mode: 'readonly', uid: this.item()!.uid, version: this.reservationVersion() });
    }
  }

  private calculatedDataSignature(): string {
    const g = this.form.controls.general.controls;
    return JSON.stringify([
      g.brokerClientId.value?.code ?? null,
      g.pickupLocationUid.value?.uid ?? null,
      g.dropOffLocationUid.value?.uid ?? null,
      g.dateOut.value,
      g.dateIn.value
    ]);
  }

  canLoadValueItems(): boolean {
    const g = this.form.controls.general.controls;
    return !!(
      g.dateOut.value &&
      g.dateIn.value &&
      g.pickupLocationUid.value &&
      g.dropOffLocationUid.value &&
      g.brokerClientId.value
    );
  }

  onNavChange(event: NgbNavChangeEvent) {
    if (event.nextId === 'values' && this.mode() === 'create') {
      this.autoLoadCalculatedData();
    }
  }

  private autoLoadCalculatedData() {
    if (!this.canLoadValueItems()) {
      this.valueItemsError =
        'Fill in the pick-up and drop-off locations and dates and the broker client to load available items.';
      return;
    }

    const signature = this.calculatedDataSignature();
    // Already loaded for these exact inputs — nothing to fetch.
    if (signature === this._lastLoadSignature) {
      this.valueItemsError = null;
      return;
    }

    this.loadCalculatedData(signature);
  }

  private tryLoadCalculatedData() {
    if (this.mode() !== 'create') return;
    if (!this.calculatedDataInputsValid()) {
      this.resetAvailableVehicleCategories();
      return;
    }
    const signature = this.calculatedDataSignature();
    if (signature === this._lastLoadSignature) return;
    this.loadCalculatedData(signature);
  }

  // The pick-up/drop-off locations and dates and the broker client determine which car
  // groups are available.
  private calculatedDataInputsValid(): boolean {
    const g = this.form.controls.general.controls;
    return (
      g.brokerClientId.valid &&
      g.pickupLocationUid.valid &&
      g.dropOffLocationUid.valid &&
      g.dateOut.valid &&
      g.dateIn.valid
    );
  }

  private resetAvailableVehicleCategories() {
    if (!this._calculatedVehicleCategories().length && this._lastLoadSignature === null) return;
    this._calcData.reset();
    this._lastLoadSignature = null;
    // Drop the chosen car group — it belongs to inputs that are no longer valid.
    this.form.controls.general.controls.vehicleCategoryUid.setValue(null);
  }

  loadCalculatedData(signature: string = this.calculatedDataSignature()) {
    this.errors = null;
    const { general } = this.form.getRawValue();
    this.loadingValueItems = true;

    this._calcData
      .load({
        brokerClientId: general.brokerClientId!.code,
        pickupLocationUid: general.pickupLocationUid!.uid,
        dateOut: this.formatDate(general.dateOut!),
        dropOffLocationUid: general.dropOffLocationUid!.uid,
        dateIn: this.formatDate(general.dateIn!)
      })
      .pipe(finalize(() => (this.loadingValueItems = false)))
      .subscribe({
        next: (vehicleCategories) => {
          this._lastLoadSignature = signature;
          // Drop a previously chosen car group that is no longer available for these inputs.
          const vehicleCategoryCtrl = this.form.controls.general.controls.vehicleCategoryUid;
          if (vehicleCategoryCtrl.value && !vehicleCategories.some((g) => g.uid === vehicleCategoryCtrl.value)) {
            vehicleCategoryCtrl.setValue(null);
          }
          // New inputs → the previous extras selection no longer applies.
          this.form.controls.general.controls.extraItems.setValue([]);
        },
        error: (e) => {
          const message = getErrorMessages(e).join(' ');
          this.errors = [message];
        }
      });
  }

  enableCreatingForm() {
    this.form.enable();
    const g = this.form.controls.general.controls;
    g.status.disable();
    g.servedVehicleCategoryUid.disable();
    this.form.controls.payment.disable();
    this.form.markAllAsTouched();
  }

  enableUpdatingForm() {
    this.form.disable();
    const g = this.form.controls.general.controls;
    g.status.enable();
    g.observations.enable();
    if (!this.hasBeenPickedUp()) {
      g.servedVehicleCategoryUid.enable();
      g.dateIn.enable();
    }
  }

  // A picked-up reservation is frozen apart from status, observations and drivers: AddCar staff can
  // still open it for editing, but nothing that reprices the rental may change.
  hasBeenPickedUp(): boolean {
    const item = this.item();
    return !!item && new Date(item.dateOut) <= new Date();
  }

  canEditDropOffDate(): boolean {
    return !!this.item() && !this.hasBeenPickedUp();
  }

  canEditValueItems(): boolean {
    return this.mode() === 'update' && !this.hasBeenPickedUp();
  }

  // Filters the current car group's catalog by the typed query (empty query → whole catalog).
  // The same extra may be picked more than once (the autocomplete runs with [unique]="false").
  filterExtras(event: AutoCompleteCompleteEvent) {
    const query = event.query.toLowerCase();
    this.extraSuggestions = this.availableValueItems().filter((e) => e.code.toLowerCase().includes(query));
  }

  submit() {
    this.errors = null;
    if (this.form.invalid) return;

    const config = this.config();
    if (config?.mode === 'create') {
      this.createReservation(this.buildCreateModel());
      return;
    }
    if (config?.mode === 'update') {
      const data: ReservationUpdateModel = { uid: this.item()!.uid, ...this.createModelValues() };

      // Pre-paid (PP) reservations warn before anything that reprices them is changed.
      if (this.requiresPpModificationWarning()) {
        const modalRef = this._modalService.open(ConfirmDialogComponent, { centered: true });
        modalRef.componentInstance.message = "Are you sure you want to modify pre-paid reservation's items?";
        modalRef.componentInstance.confirmBtnText = 'Modify';
        modalRef.componentInstance.style = 'danger';
        modalRef.closed.subscribe(() => this.update(data));
        return;
      }

      this.update(data);
    }
  }

  private requiresPpModificationWarning(): boolean {
    if (this.item()?.reservationType !== ReservationType.PP) return false;
    return this._extrasModified || this.daysChanged() || this.additionalDriversChanged();
  }

  private additionalDriversChanged(): boolean {
    const item = this.item();
    if (!item) return false;
    return countAdditionalDrivers(this.drivers()) !== countAdditionalDrivers(item.drivers ?? []);
  }

  private daysChanged(): boolean {
    const item = this.item();
    const dateIn = this.form.controls.general.controls.dateIn.value;
    if (!dateIn || !item) return false;
    return new Date(item.dateIn).getTime() !== dateIn.getTime();
  }

  dismiss() {
    if (this.hasUnsavedData) {
      const confirmClose = confirm('You have unsaved changes. Are you sure you want to close?');
      if (!confirmClose) return;
    }
    this._modal.dismiss();
  }

  openRaCreation() {
    this._modalService.dismissAll('quite');
    const options: NgbModalOptions = {
      fullscreen: 'xxl',
      scrollable: true,
      size: 'xl',
      centered: true,
      modalDialogClass: 'align-items-stretch'
    };
    const modalRef = this._modalService.open(RentalAgreementsUpsertDialogComponent, options);
    modalRef.componentInstance.config.set({ mode: 'create', reservationNumber: this.item()!.number });
    return modalRef.closed;
  }

  createClosedRa() {
    this.loading = true;
    of(true)
      .pipe(
        switchMap(() => this.openCreateRaDialog()),
        switchMap(() => this._raService.createRentalAgreementFromReservation(this.item()!.uid)),
        catchError((e) => {
          this.errors = getErrorMessages(e);
          return EMPTY;
        }),
        finalize(() => (this.loading = false)),
        tap(() => this._selectedReservation$.subscribe()),
        switchMap((response) => this.openGoToRaDialog(response))
      )
      .subscribe({
        next: (result) => {
          this.navigateToRa(result.uid);
        },
        error: (e) => (this.errors = getErrorMessages(e))
      });
  }

  navigateToRa(rentalAgreementUid: string) {
    this._modal.dismiss();
    this._router.navigate(['/operations/rental-agreements'], { queryParams: { selected: rentalAgreementUid } });
  }

  openMainDriverDialog() {
    const readonly = this.mode() === 'readonly';
    const existing = this.drivers().find((d) => !d.isAdditionalDriver);

    const options: NgbModalOptions = { size: 'lg', centered: true, scrollable: true, fullscreen: 'md' };
    const modalRef = this._modalService.open(ReservationDriversUpsertDialogComponent, options);
    modalRef.componentInstance.config.set(
      existing
        ? readonly
          ? { mode: 'readonly', driver: existing, reservationVersion: this.reservationVersion() }
          : { mode: 'update', driver: existing, onlyAdditionalDriver: false }
        : { mode: 'create', reservationUid: this.uid(), onlyAdditionalDriver: false }
    );
    modalRef.closed.subscribe((value: ReservationDriverDetailsModel) => {
      this.drivers.update((list) =>
        existing
          ? list.map((d) => (d.uid === existing.uid ? { ...value, uid: existing.uid } : d))
          : [...list, { ...value, uid: newClientUid() }]
      );
      this.onDriversChanged();
      this.driversComponent()?.refresh();
    });
  }

  private openGoToRaDialog(response: CreateResponseModel): Observable<CreateResponseModel> {
    const createdModal = this._modalService.open(ConfirmDialogComponent, { centered: true });
    createdModal.componentInstance.title = 'Rental agreement successfully created';
    createdModal.componentInstance.message = 'Do you want to proceed to viewing the created agreement?';
    createdModal.componentInstance.confirmBtnText = 'Go to agreement';
    createdModal.componentInstance.style = 'success';

    return createdModal.closed.pipe(map(() => response));
  }

  private openCreateRaDialog(): Observable<unknown> {
    const modalRef = this._modalService.open(ConfirmDialogComponent, { centered: true });
    modalRef.componentInstance.message =
      'Are you sure you want to create and close a rental agreement for this reservation?';
    modalRef.componentInstance.confirmBtnText = 'Create';
    modalRef.componentInstance.style = 'success';

    return modalRef.closed;
  }

  private initialize() {
    const item = this.item();
    if (!item) return;
    this.form.reset();
    this.form.setValue({
      general: {
        status: item.status,
        dateOut: new Date(item.dateOut),
        dateIn: new Date(item.dateIn),
        pickupLocationUid: null,
        dropOffLocationUid: null,
        vehicleCategoryUid: item.vehicleCategoryUid,
        brokerClientId: null,
        servedVehicleCategoryUid: item.servedVehicleCategoryUid,
        brokerReference: item.brokerReference,
        observations: item.observations ?? '',
        // Create-only field; not used in update/readonly.
        extraItems: []
      },
      payment: {
        currency: item.currency,
        taxRate: item.taxRate,
        totalTaxValue: this.toDecimal(item.totalTaxValue),
        cdwExcess: this.toDecimal(item.cdwExcess),
        tpExcess: this.toDecimal(item.tpExcess),
        areKmsIncluded: item.areKmsIncluded,
        areKmsFreePerDay: item.areKmsFreePerDay,
        freeKmsPerDay: item.freeKmsPerDay,
        additionalKmsPrice: this.toDecimal(item.additionalKmsPrice),
        discountAmount: this.toDecimal(item.discountAmount)
      }
    });

    this.drivers.set(item.drivers ?? []);
    this.valueItems.set(item.valueItems ?? []);

    if (item.pickupLocationUid) {
      this._locationService
        .getLocationByCodeOrUid(item.pickupLocationUid)
        .pipe(
          tap((loc) => {
            this.form.controls.general.controls.pickupLocationUid.setValue({
              uid: loc.uid,
              selectedLabel: `${loc.referenceCode} - ${loc.name}`
            });
          }),
          switchMap((loc) => this._raService.isConfiguredForCountry(loc.countryUid)),
          tap((isIsConfigured) => (this._raCreationConfiguredForCountry = isIsConfigured))
        )
        .subscribe();
    }

    if (item.dropOffLocationUid) {
      this._locationService.getLocationByCodeOrUid(item.dropOffLocationUid).subscribe((loc) => {
        this.form.controls.general.controls.dropOffLocationUid.setValue({
          uid: loc.uid,
          selectedLabel: `${loc.referenceCode} - ${loc.name}`
        });
      });
    }

    if (item.brokerClientId != null) {
      this._brokerService.getBrokerByCode(item.brokerClientId).subscribe((broker) => {
        this.form.controls.general.controls.brokerClientId.setValue({
          code: broker.code,
          selectedLabel: `${broker.code} - ${broker.name}`
        });
      });
    }

    if (this.mode() !== 'create') {
      // The grids may already be rendered; re-list them from the freshly seeded arrays.
      setTimeout(() => {
        this.driversComponent()?.refresh();
        this.valuesComponent()?.refresh();
      });
    }

    if (this.mode() === 'update' && !this.hasBeenPickedUp()) {
      this.loadCalculatedDataForCurrentReservation();
    }

    this.hasUnsavedData = false;
    this._extrasModified = false;
    this.historyExpanded = 0;
  }

  // Update mode: the editable served car group should offer the groups available for the
  // reservation's own dates/locations/broker, so load them from calculatedData. A picked-up
  // reservation keeps that field frozen, so it is not loaded at all.
  private loadCalculatedDataForCurrentReservation() {
    const item = this.item();
    if (!item) return;
    this.loadingValueItems = true;
    this.valueItemsError = null;

    this._calcData
      .load({
        brokerClientId: item.brokerClientId,
        pickupLocationUid: item.pickupLocationUid,
        dateOut: item.dateOut,
        dropOffLocationUid: item.dropOffLocationUid,
        dateIn: item.dateIn
      })
      .pipe(finalize(() => (this.loadingValueItems = false)))
      .subscribe({
        error: (e) => (this.valueItemsError = getErrorMessages(e).join(' '))
      });
  }

  // The acceptance itself lives in the values store; here we only track that extras were touched
  // (drives the pre-paid warning and enables Save).
  onValueAcceptedChanged() {
    this._extrasModified = true;
    this.form.markAsDirty();
    this.hasUnsavedData = true;
  }

  onDriversChanged() {
    if (this.mode() !== 'update') return;
    this._driversModified = true;
    this.form.markAsDirty();
    this.hasUnsavedData = true;
  }

  private createReservation(data: ReservationCreateModel) {
    this.loading = true;
    this._reservationsService
      .createReservation(data)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (result) => {
          this.valueItems.set([]);
          this.drivers.set([]);
          this._toastsService.showSuccess('Reservation created successfully.');
          this._modal.dismiss();
          this._router.navigate(['/operations/reservations'], { queryParams: { selected: result.uid } });
        },
        error: (e) => (this.errors = getErrorMessages(e))
      });
  }

  private buildCreateModel(): ReservationCreateModel {
    const { general } = this.form.getRawValue();
    const drivers = this.drivers();

    return {
      dateOut: this.formatDate(general.dateOut!),
      dateIn: this.formatDate(general.dateIn!),
      pickupLocationUid: general.pickupLocationUid!.uid,
      dropOffLocationUid: general.dropOffLocationUid!.uid,
      vehicleCategoryUid: general.vehicleCategoryUid!,
      servedVehicleCategoryUid: general.servedVehicleCategoryUid || null,
      brokerClientId: general.brokerClientId!.code,
      brokerReference: general.brokerReference,
      observations: general.observations || '',
      // Collapse client-only temp uids to the empty guid; the backend field is a Guid ("new" = empty).
      drivers: drivers.map((d) => ({ ...d, uid: toBackendUid(d.uid) })),
      // The extras picked in the "Extra items" multiselect are the accepted value items.
      valueItems: (general.extraItems ?? []).map((v) => ({ code: v.code, isAccepted: true }))
    };
  }

  private update(data: ReservationUpdateModel) {
    this.loading = true;
    this._reservationsService
      .updateReservation(data)
      // Re-fetch the saved reservation so the form and grids reflect the server-recalculated state
      // (totals, versions, resolved children) instead of the locally-staged edits.
      .pipe(
        switchMap(() => this._reservationsService.getReservationDetails(data.uid)),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (reservation) => {
          this.item.set(reservation);
          this.config.set({ mode: 'readonly', uid: data.uid, version: 'latest' });
          this.initialize();
          this._toastsService.showSuccess('Reservation updated successfully.');
        },
        error: (e) => {
          this.errors = getErrorMessages(e);
        }
      });
  }

  private createModelValues(): Omit<ReservationUpdateModel, 'uid'> {
    const { general } = this.form.getRawValue();
    return {
      status: general.status,
      servedVehicleCategoryUid: general.servedVehicleCategoryUid,
      observations: general.observations ?? '',
      dateIn: this.formatDate(general.dateIn!),
      valueItems: this.valueItems()
        .filter((v) => v.isAccepted && v.code.toLowerCase() !== 'dayvalue')
        .map((v) => ({ code: v.code, isAccepted: true })),
      // Collapse client-only temp uids (newly added drivers) to the empty guid so the Guid field binds.
      drivers: this.drivers().map((d) => ({ ...d, uid: toBackendUid(d.uid) }))
    };
  }

  private formatDate(dateTime: Date): string {
    return new Date(
      Date.UTC(
        dateTime.getFullYear(),
        dateTime.getMonth(),
        dateTime.getDate(),
        dateTime.getHours(),
        dateTime.getMinutes(),
        dateTime.getSeconds(),
        dateTime.getMilliseconds()
      )
    ).toISOString();
  }

  private addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  private atDefaultHour(date: Date): Date {
    const result = new Date(date);
    result.setHours(DEFAULT_RENTAL_HOUR, 0, 0, 0);
    return result;
  }

  private addMinutes(date: Date, minutes: number): Date {
    const result = new Date(date);
    result.setMinutes(result.getMinutes() + minutes);
    return result;
  }

  private toDecimal(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  private getInvalidControls(group: AbstractControl, path = ''): string[] {
    if (group instanceof FormControl) return group.invalid ? [path] : [];
    if (group instanceof FormGroup) {
      return Object.entries(group.controls).flatMap(([key, ctrl]) =>
        this.getInvalidControls(ctrl, path ? `${path}.${key}` : key)
      );
    }
    return [];
  }

  // --- Template helpers (bound directly in the view) ---

  getReservationTypeValueName() {
    const item = this.item();
    return item ? ReservationType[item.reservationType] : '';
  }

  getValueGridMinHeight() {
    return window.innerWidth < 576 ? 40 : 32;
  }

  getStatusesOptions(): { label: string; value: ReservationStatus }[] {
    const icons = [
      'fas fa-circle-check text-success',
      'fa-regular fa-eye-slash text-secondary',
      'fa-solid fa-ban text-warning',
      'fas fa-hourglass-half text-success',
      'fa-regular fa-times-circle text-secondary'
    ];

    return Object.values(ReservationStatus)
      .filter((x) => !isNaN(Number(x)))
      .map((status) => ({
        label: ReservationStatusesDisplayNames[status as ReservationStatus],
        value: status as ReservationStatus,
        icon: icons[status as ReservationStatus]
      }));
  }

  generalTabInvalid() {
    return this.form.enabled && this.form.controls.general.invalid && this.form.controls.general.touched;
  }

  generalTabModified() {
    return (
      this.mode() === 'update' &&
      this.form.enabled &&
      this.form.controls.general.valid &&
      this.form.controls.general.touched
    );
  }

  paymentTabInvalid() {
    return this.item() && this.form.enabled && this.form.controls.payment.invalid && this.form.controls.payment.touched;
  }

  paymentTabModified() {
    return (
      this.mode() === 'update' &&
      this.form.enabled &&
      ((this.form.controls.payment.valid && this.form.controls.payment.dirty) || this._extrasModified)
    );
  }

  driversTabInvalid() {
    return this.drivers().length === 0;
  }

  driversTabModified() {
    return this.mode() === 'update' && this._driversModified;
  }

  get allowToCreateClosedRa() {
    const item = this.item();
    return (
      !!item &&
      // Only the latest version can spawn a rental agreement — older versions are read-only history.
      this.reservationVersion() === 'latest' &&
      !item.rentalAgreementNumber &&
      this._raCreationConfiguredForCountry &&
      item.status !== ReservationStatus.Cancelled &&
      item.status !== ReservationStatus.NotConfirmed &&
      this._authService.IsAddCarUser
    );
  }

  get allowToCreateRa() {
    const item = this.item();
    return (
      !!item &&
      // Only the latest version can spawn a rental agreement — older versions are read-only history.
      this.reservationVersion() === 'latest' &&
      !item.rentalAgreementNumber &&
      this._raCreationConfiguredForCountry &&
      item.status !== ReservationStatus.Cancelled &&
      item.status !== ReservationStatus.NotConfirmed
    );
  }
}
